import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  ArrowLeft,
  RefreshCw,
  UtensilsCrossed,
  CalendarX,
  Loader2,
  Wifi,
  ChevronRight,
} from "lucide-react";
import { getNotifications } from "@/services/parent";
import type { Notification } from "@/components/notifications/NotificationsDropdown";
import { getNotifNavPath } from "@/components/notifications/NotificationsDropdown";
import ParentHeader from "@/components/common/ParentHeader";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useRoleContext } from "@/hooks/useRoleContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function notifMeta(title: string): {
  Icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  accentBar: string;
  tagBg: string;
  tagText: string;
} {
  const t = title.toLowerCase();
  if (t.includes("lunch") || t.includes("food")) {
    return {
      Icon: UtensilsCrossed,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-500",
      accentBar: "bg-amber-400",
      tagBg: "bg-amber-50 border-amber-200",
      tagText: "text-amber-700",
    };
  }
  if (t.includes("subscription") || t.includes("expir")) {
    return {
      Icon: CalendarX,
      iconBg: "bg-rose-50",
      iconColor: "text-rose-500",
      accentBar: "bg-rose-400",
      tagBg: "bg-rose-50 border-rose-200",
      tagText: "text-rose-700",
    };
  }
  return {
    Icon: Bell,
    iconBg: "bg-brand-50",
    iconColor: "text-brand-500",
    accentBar: "bg-brand-400",
    tagBg: "bg-brand-50 border-brand-200",
    tagText: "text-brand-700",
  };
}

// ─── Full notification card ───────────────────────────────────────────────────

function NotificationCard({
  notif,
  index,
  role,
}: {
  notif: Notification;
  index: number;
  role: string;
}) {
  const navigate = useNavigate();
  const { Icon, iconBg, iconColor, accentBar, tagBg, tagText } = notifMeta(notif.Title);
  const navPath = getNotifNavPath(notif.Screen_Name, role);

  return (
    <div
      className={`relative bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow ${navPath ? "cursor-pointer" : ""}`}
      onClick={() => navPath && navigate(navPath)}
    >
      {/* Left accent bar */}
      <div className={`absolute top-0 left-0 h-full w-1 ${accentBar}`} />

      <div className="flex gap-4 px-5 py-4 pl-6">
        {/* Icon */}
        <div
          className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}
        >
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h3 className="text-[14px] font-bold text-gray-900 leading-snug">
              {notif.Title}
            </h3>
            <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
              <span className="text-[10px] font-semibold text-gray-400">#{index + 1}</span>
              {navPath && <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
            </div>
          </div>

          <p className="text-[13px] text-gray-600 leading-relaxed">
            {notif.Message}
          </p>

          {notif.Child_Name?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              <span className="text-[11px] text-gray-400 font-medium self-center">
                For:
              </span>
              {notif.Child_Name.map((name) => (
                <span
                  key={name}
                  className={`inline-flex items-center px-2 py-0.5 rounded-lg border text-[11px] font-semibold capitalize ${tagBg} ${tagText}`}
                >
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const userDetails = useCurrentUser();
  const username = userDetails?.username || "";
  const { role } = useRoleContext();
  const isParent = role === "parent";
  const isDelivery = role === "deliveryboy" || role.includes("delivery");
  
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(false);
    try {
      const res = await getNotifications(username);
      if (res?.status === 200 && Array.isArray(res.data)) {
        setNotifications(res.data as Notification[]);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData(true);
    setRefreshing(false);
  };

  return (
    <div className="min-h-screen bg-[#F0F2FF]">
      {isParent && <ParentHeader />}
      {isDelivery && (
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="text-base font-bold text-gray-900">Notifications</span>
        </header>
      )}

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex items-center gap-4 mb-6">
          {!isDelivery && (
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}

          <div className="flex-1">
            {!isDelivery && (
              <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                Notifications
              </h1>
            )}
            {!loading && !error && (
              <p className="text-sm text-gray-500 mt-0.5">
                {notifications.length === 0
                  ? "No notifications"
                  : `${notifications.length} notification${notifications.length !== 1 ? "s" : ""}`}
              </p>
            )}
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="w-9 h-9 rounded-xl bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Loader2 className="w-8 h-8 text-gray-300 animate-spin" />
            <p className="text-sm text-gray-400">Loading notifications…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <Wifi className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-base font-bold text-gray-700">
              Couldn't load notifications
            </p>
            <p className="text-sm text-gray-400">
              Check your connection and try again
            </p>
            <button
              onClick={() => loadData()}
              className="flex items-center gap-2 px-5 py-2 mt-1 bg-[#D4AF37] hover:bg-[#c9a52e] text-[#1A1F3C] text-sm font-bold rounded-xl transition-colors shadow-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-20 h-20 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center">
              <Bell className="w-10 h-10 text-gray-200" />
            </div>
            <p className="text-base font-bold text-gray-700">All caught up!</p>
            <p className="text-sm text-gray-400 text-center max-w-xs">
              You have no notifications at the moment. We'll let you know when
              something needs your attention.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((n, i) => (
              <NotificationCard key={i} notif={n} index={i} role={role} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
